import { Assets, Container, Polygon, Rectangle, Sprite, Text, TilingSprite, type Texture } from 'pixi.js';
import { BIOMES, isSlotUnlocked, type RealmView } from '@goodtime/shared';
import {
  FLY_MS,
  PaintedMapScene,
  boxOf,
  type MapInsets,
  type PaintedMapOptions,
  type Placed,
} from './PaintedMapScene';
import type { Camera } from '../logic/realm/camera';
import { worldOf, worldSlots } from '../logic/realm/places';
import { crownAnchor, DOT_STEP, NO_INSET } from '../logic/realm/mapHud';
import { placeLabels, type Rect } from '../logic/realm/labels';
import { spotWorld } from '../logic/realm/spots';
import { GLINT_IDS, RING_R, artAt, focusOrder, glintScale, layoutPins, slidePlaces, spotAt } from '../logic/realm/overviewPlaces';
import {
  apronAbove,
  apronBelow,
  FADE_H,
  FIRST_VIEW_SHARE,
  FADE_SLICES,
  FADE_SLICE_W,
  LAMP_LIT_URL,
  LAMP_UNLIT_URL,
  PANEL_LINE,
  PARCHMENT_TILE,
  PARCHMENT_URL,
  STRIP_H,
  STRIP_TILES,
  STRIP_W,
  STRIP_WORLDS,
  fadeBottomUrl,
  fadeUrl,
  homeStrip,
  lampMoment,
  lampLine,
  litWorlds,
  scenesIn,
  freshInView,
  stripIndexOf,
  stripPositions,
  tapPoint,
  tileMarginX,
  vignetteUrl,
  type StripWorld,
} from '../logic/realm/overview';
import {
  clampStrip,
  dragBounds,
  idealX,
  releaseSpeed,
  SCROLL_TAIL_MS,
  scrollTail,
  scrollWorld,
  snapWorld,
  stepWorld,
  stopCamera,
  stripLayout,
  toScreen,
  viewRect,
  zoomedCamera,
  type StripLayout,
} from '../logic/realm/overviewCamera';
import { softDot } from '../widgets/realm/RealmAmbient';
import { OverviewPanel, StripDots, StripNote } from '../widgets/realm/OverviewPanel';
import { StripLamps, type LampState } from '../widgets/realm/StripLamps';
import { RealmPlaces, type PlaceCardUi, type RealmPlacesOptions } from '../widgets/realm/RealmPlaces';
import type { MiniGameSnapshot } from '../widgets/minigames/types';
import type { Lamp } from '../logic/realm/overview';
import type { RealmOverview } from './RealmMapScene';
import { uiStyle } from '../widgets/board';

/** A snap to a stop takes about this long (a glide into a world is {@link FLY_MS}). */
const SNAP_MS = 400;
/** Wheel events closer together than this are one gesture (a notch's tail, a smooth wheel, a trackpad's inertia). */
const WHEEL_GAP_MS = 180;
/** Names keep this far from the screen's edges. */
const EDGE = 16;
/** A fling to a world whose picture has not loaded waits this long for it at most. */
const SCENE_WAIT_MS = 800;
/** A trackpad's scroll snaps once it has been still this long. */
const SCROLL_REST_MS = 140;
/** How far the parchment apron (and the presses the strip takes) reaches past the painting all round, master pixels. */
const APRON_REACH = 6000;
/** A pin fades in or out over this long when a stop shows it or hides it. */
const PIN_FADE_MS = 150;

/** Where the player's lit lamps are remembered, and which worlds a realm view says were played (both supplied by the app). */
export interface StripLampsMemory {
  /** The worlds (indexes into BIOMES) the player has played a slot in, from the realm view. */
  worldsPlayed(view: RealmView): number[];
  /** The lit worlds remembered on this device (indexes into BIOMES). */
  load(): number[];
  save(worlds: number[]): void;
}

export interface OverviewSceneOptions extends PaintedMapOptions {
  /** Open a world's map, as the realm overview's. Required: the strip is the overview. */
  overview: RealmOverview;
  places?: RealmPlacesOptions;
  lamps?: StripLampsMemory;
  /** The world the strip opens on, and whether it opens zoomed on it and eases out (back from that world's map). */
  opening?: { world: number; zoomed: boolean };
}

const still = (): boolean => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

/**
 * The traveller's map (flag `overview_strip`): the overview of the seven worlds as one painted strip, panned along x only, snapping
 * to each world. A tap on the chosen world's scene or its Visit button glides in and opens its map; a tap on a side scene, a corner
 * label or a phone dot centres that world first. The painting, tiles, HUD, floors and name placing are the painted map's
 * (PaintedMapScene); the strip adds its camera (logic/realm/overviewCamera), the worlds' scenes, lamps, the chosen world's panel and
 * the dots and corner labels. Side places and secrets keep their positions in logic/realm/overviewData for the places stream.
 */
export class OverviewScene extends PaintedMapScene<OverviewSceneOptions> {
  protected override readonly logName = 'OverviewScene';
  private lay: StripLayout | null = null;
  /** The places no stop shows at this size with their rings on their spots: they slide theirs (overviewPlaces `slidePlaces`). */
  private slide: ReadonlySet<string> = new Set();
  /** The chosen world, as an index into STRIP_WORLDS (west to east). */
  private chosen: number;
  /** The camera's x as the finger has dragged it, before the painting's ends clamp it. */
  private vx = 0;
  private dragging = false;
  private dragFrom = 0;
  private moves: { t: number; x: number }[] = [];
  private scrollTimer: ReturnType<typeof setTimeout> | null = null;
  /** Where the camera was when a sideways scroll began. */
  private scrollFrom = 0;
  /** Until then (ms, the events' clock), a small sideways event is the last scroll's tail. */
  private scrollQuiet = 0;
  private lastWheel = 0;
  /** A glide is waiting for the target scene's picture (a fling to a world not yet loaded). */
  private flightWait = false;
  private flightToken = 0;
  /** The strip index of the world the player's own slot is in (the offline lamps' fallback). */
  private readonly homeIdx: number;
  /** The world being glided into (strip index), before its map opens. */
  private entering: number | null = null;
  private enterTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly reduced = still();

  private readonly apron = new Container();
  private readonly fadeLayer = new Container();
  private readonly sceneLayer = new Container();
  private readonly lampLayer = new Container();
  private readonly sceneSprites: (Sprite | null)[] = STRIP_WORLDS.map(() => null);
  private readonly sceneWanted = new Set<number>();
  private readonly sceneLoadOf = new Map<number, Promise<void>>();
  private readonly fadeSprites = new Map<number, Sprite>();
  private readonly fadeWanted = new Set<number>();
  private readonly fadeBottomSprites = new Map<number, Sprite>();
  private readonly fadeBottomWanted = new Set<number>();
  private parchment: Texture | null = null;
  private lampArt: { unlit: Texture; lit: Texture; dot: Texture } | null = null;
  private lamps: StripLamps | null = null;
  private pendingFresh = new Set<number>();
  private remembered: number[] = [];
  private panel: OverviewPanel | null = null;
  private panelSpot: Placed | null = null;
  private labelSpots: Placed[] = [];
  private dots: StripDots | null = null;
  private note: StripNote | null = null;
  private places: RealmPlaces | null = null;
  /** The side places' pins, over everything on the painting. A pin stands for the stop the camera is on or going to (see
   *  {@link layPins}); it moves with the painting through a drag and a glide, and fades when a stop changes it. */
  private readonly spotLayer = new Container();
  private pinTargets = new Map<string, boolean>();
  private prefetch = false;
  private readonly sceneLoads: Promise<void>[] = [];
  private easeOut: boolean;
  private firstViewDoneAt: number | null = null;

  constructor(container: HTMLElement, opts: OverviewSceneOptions) {
    const world = opts.opening?.world ?? worldOf(opts.currentNodeId);
    super(container, opts, STRIP_TILES, world);
    const i = stripIndexOf(world);
    this.chosen = i >= 0 ? i : stripIndexOf(0) >= 0 ? stripIndexOf(0) : 0;
    this.currentWorld = STRIP_WORLDS[this.chosen]!.biome;
    this.homeIdx = homeStrip(worldOf(opts.currentNodeId));
    this.easeOut = !!opts.opening?.zoomed && !this.reduced;
  }

  // ---- the painted map's hooks: one axis, no pinch, no zoom ----

  protected override clampView(cam: Camera): Camera {
    return this.lay ? clampStrip(cam, this.view, this.lay) : cam;
  }

  protected override dragBy(dx: number, _dy: number): void {
    if (!this.lay || this.entering !== null) return;
    if (!this.dragging) {
      this.dragging = true;
      this.dismissTitle();
      this.flightToken++;
      this.flightWait = false;
      this.dragFrom = this.chosen;
      this.moves = [];
      const ideal = idealX(this.lay, this.chosen);
      this.vx = Math.abs(stopCamera(this.lay, this.view, this.chosen).x - this.cam.x) < 1 ? ideal : this.cam.x;
    }
    const b = dragBounds(this.lay);
    this.vx = Math.min(b.max, Math.max(b.min, this.vx + dx));
    this.moves.push({ t: performance.now(), x: this.vx });
    if (this.moves.length > 12) this.moves.shift();
    this.apply({ ...this.cam, x: this.vx });
  }

  protected override allowsPinch(): boolean {
    return false;
  }

  protected override wantsZoomButtons(): boolean {
    return false;
  }

  protected override tileMargin(zoom: number): { x: number; y: number } {
    return { x: tileMarginX(zoom, this.view.w, this.chosen, this.prefetch), y: 0 };
  }

  protected override wheelAt(e: WheelEvent, _at: { x: number; y: number }): void {
    if (!this.lay) return;
    // The painted map stops any glide before this runs: whatever is not acted on below, the glide goes on.
    // A dialog over the strip (the host covers it) has the wheel too, as it has the keys.
    if (this.entering !== null || e.ctrlKey || this.hostPaused) {
      // A world is being entered, or a trackpad pinch (ctrl and wheel): nothing steps.
      this.resumeFlight();
      return;
    }
    // The event's own time (not the moment it is handled: a busy frame can hold a gesture's events back and run them together).
    const now = e.timeStamp > 0 ? e.timeStamp : performance.now();
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      // A trackpad's sideways scroll: the painting follows, then snaps once it rests.
      if (this.scrollTimer === null && scrollTail(e.deltaX, now, this.scrollQuiet)) {
        // The settled scroll's inertia, still coming: it would start a second scroll mid-glide. The glide goes on.
        this.scrollQuiet = now + SCROLL_TAIL_MS;
        this.resumeFlight();
        return;
      }
      const b = dragBounds(this.lay);
      if (this.scrollTimer === null) {
        this.dismissTitle();
        this.vx = this.onStopNow() ? idealX(this.lay, this.chosen) : this.cam.x;
        this.scrollFrom = this.vx;
        this.dragFrom = this.chosen;
        this.moves = [];
        this.flightToken++;
        this.flightWait = false;
      }
      this.vx = Math.min(b.max, Math.max(b.min, this.vx - e.deltaX));
      this.apply({ ...this.cam, x: this.vx });
      if (this.scrollTimer) clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => {
        this.scrollTimer = null;
        this.scrollQuiet = performance.now() + SCROLL_TAIL_MS;
        // A swipe that went somewhere turns at least one world, though the nearest stop is still the one it began on.
        if (this.lay) this.settleOn(scrollWorld(this.lay, this.vx, this.scrollFrom, this.dragFrom));
      }, SCROLL_REST_MS);
      return;
    }
    if (this.scrollTimer !== null || now < this.scrollQuiet) {
      // A sideways scroll (or its tail) that drifted up or down for an event: part of that scroll, not a step.
      if (this.scrollTimer === null) this.resumeFlight();
      return;
    }
    // A vertical wheel or scroll steps one world per gesture: a notch's tail, a smooth wheel's stream or a trackpad's inertia are one
    // gesture while the events keep coming, and a step starts only after a pause.
    const gap = now - this.lastWheel;
    this.lastWheel = now;
    if (e.deltaY === 0 || gap < WHEEL_GAP_MS) {
      this.resumeFlight();
      return;
    }
    this.step(e.deltaY > 0 ? 1 : -1);
  }

  protected override pressEnded(): void {
    if (!this.dragging) {
      // A tap (or a press that never reached a drag): the press stopped any glide on its way down, so carry on to the stop.
      this.resumeFlight();
      return;
    }
    this.dragging = false;
    if (!this.dragged) return;
    this.settleVirtual(releaseSpeed(this.moves, performance.now()));
  }

  /** The camera is at its chosen stop (to half a pixel), at the layout's zoom. */
  private onStopNow(): boolean {
    if (!this.lay) return false;
    const to = stopCamera(this.lay, this.view, this.chosen);
    return Math.abs(this.cam.x - to.x) < 0.5 && Math.abs(this.cam.y - to.y) < 0.5 && Math.abs(this.cam.zoom - to.zoom) < 1e-4;
  }

  /** Something stopped a glide (a press, a wheel event that did nothing): go on to where the scene is meant to be, the stop of the
   *  chosen world or, while a world is being entered, the zoomed scene. */
  private resumeFlight(ms = SNAP_MS): void {
    if (!this.lay || this.dragging || this.scrollTimer !== null || this.flightWait) return;
    if (this.entering !== null) {
      // (Reduced motion never zooms in: a jump to the zoomed scene would flash it for a frame before the map opens.)
      if (!this.reduced) this.flyTo(zoomedCamera(this.lay, this.view, this.entering), FLY_MS);
      return;
    }
    if (!this.onStopNow()) this.flyTo(stopCamera(this.lay, this.view, this.chosen), ms);
  }

  protected override onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.escapeOverlay()) return;
      if (this.boardUp()) this.closeBoard();
      return;
    }
    // Keys belong to a dialog over the strip, to a field or a button being typed in, and to a world being entered.
    if (this.covering() || this.boardUp() || this.hostPaused || this.entering !== null) return;
    if (event.metaKey || event.ctrlKey || event.altKey) return;
    const target = event.target as { tagName?: string; isContentEditable?: boolean } | null;
    const tag = target?.tagName?.toUpperCase();
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || target?.isContentEditable) return;
    if (event.key === 'ArrowLeft') this.step(-1);
    else if (event.key === 'ArrowRight') this.step(1);
    else if (event.key === 'Enter') this.visit();
  }

  // ---- what the painted map asks of its subclass ----

  protected override onResize(): void {
    const first = this.view.w === 1;
    super.onResize();
    // The first camera is on: everything it shows starts loading.
    if (first) this.loadFirstView();
  }

  /** The first view is drawn once its tiles and its scenes are in. */
  override async firstView(): Promise<void> {
    await super.firstView();
    await Promise.all(this.sceneLoads);
  }

  protected override openingSpot(): { x: number; y: number } {
    const w = STRIP_WORLDS[this.chosen]!;
    return { x: w.stop / STRIP_W, y: w.clearing.y / STRIP_H };
  }

  protected override openingCamera(): Camera {
    const lay = this.lay!;
    // Zoomed only when it will ease out (under reduced motion it opens at the stop).
    return this.easeOut
      ? zoomedCamera(lay, this.view, this.chosen)
      : stopCamera(lay, this.view, this.chosen);
  }

  protected override buildingAt(_name: string): { x: number; y: number } | null {
    return null;
  }

  protected override hasSwitcher(): boolean {
    return false;
  }

  protected override buildPlates(): void {
    this.plates = [];
    this.refreshChosen();
  }

  protected override async preload(): Promise<void> {
    await Promise.all([
      super.preload(),
      Assets.load<Texture>(PARCHMENT_URL)
        .then((t) => (this.parchment = t))
        .catch(() => undefined),
      Promise.all([Assets.load<Texture>(LAMP_UNLIT_URL), Assets.load<Texture>(LAMP_LIT_URL)])
        .then(([unlit, lit]) => {
          this.lampArt = { unlit, lit, dot: softDot() };
        })
        .catch(() => undefined),
    ]);
  }

  protected override buildMore(): void {
    const w = this.world;
    // The strip takes presses on the apron as well as on the painting: on a short screen (a landscape phone) the panel, its arrows and
    // Visit hang below the painting, and the hit test would pass them by.
    w.hitArea = new Rectangle(-APRON_REACH, -APRON_REACH, STRIP_W + APRON_REACH * 2, STRIP_H + APRON_REACH * 2);
    // The parchment apron under everything, and the top fade over it, in master pixels.
    if (this.parchment) {
      const reach = APRON_REACH;
      const apron = new TilingSprite({
        texture: this.parchment,
        width: STRIP_W + reach * 2,
        height: STRIP_H + reach * 2,
        tileScale: { x: 1, y: 1 },
      });
      apron.position.set(-reach, -reach);
      // The tile is a power of the master's pixel: keep its phase on the master's origin.
      apron.tilePosition.set(reach % PARCHMENT_TILE, reach % PARCHMENT_TILE);
      this.apron.addChild(apron);
    }
    w.addChildAt(this.apron, 0);
    w.addChildAt(this.fadeLayer, 1);
    const at = w.getChildIndex(this.labelLayer);
    w.addChildAt(this.sceneLayer, at);
    w.addChildAt(this.lampLayer, at + 1);
    this.labelLayer.visible = true;

    this.lamps = this.lampArt
      ? new StripLamps(this.lampLayer, this.lampArt, this.reduced, (world, lamp) => this.lampTapped(world, lamp))
      : null;
    this.remembered = (this.opts.lamps?.load() ?? []).flatMap((b) => {
      const i = stripIndexOf(b);
      return i >= 0 ? [i] : [];
    });
    this.lamps?.setLit(this.remembered);

    // The chosen world's panel, and the other worlds' names under their scenes.
    this.panel = new OverviewPanel(
      this.floorFor(this.currentWorld),
      false,
      () => this.visit(),
      STRIP_WORLDS.map((w) => w.name),
      this.texture('arrow'),
      (dir) => {
        if (!this.dragged && this.entering === null) this.step(dir);
      },
    );
    this.landmarkLayer.addChild(this.panel);
    this.panelSpot = {
      item: this.panel,
      mx: STRIP_WORLDS[this.chosen]!.stop,
      my: PANEL_LINE,
      drop: 0,
      box: boxOf(this.panel),
      maxShift: 150,
      tappable: true,
      shown: false,
      fresh: true,
      world: this.currentWorld,
    };
    this.labelSpots = STRIP_WORLDS.map((sw, i) => {
      const t = new Text({ text: sw.name, style: uiStyle(19, 0x5b3a1c, '600') });
      t.style.stroke = { color: 0xf3e3bc, width: 3, join: 'round' };
      t.anchor.set(0.5);
      t.eventMode = 'static';
      t.cursor = 'pointer';
      t.on('pointertap', () => {
        if (!this.dragged && this.entering === null) this.choose(i);
      });
      this.landmarkLayer.addChild(t);
      return {
        item: t,
        mx: sw.label.x,
        my: sw.label.y + 70,
        drop: 0,
        box: boxOf(t),
        maxShift: 160,
        tappable: true,
        shown: false,
        fresh: true,
      };
    });

    // The scenes: their tap areas are their painted parts.
    this.dots = new StripDots(STRIP_WORLDS.length, (i) => this.centre(i));
    this.note = new StripNote();
    this.hud.addChild(this.dots, this.note);
    if (this.opts.places) this.buildPlaces(this.opts.places);
    this.refreshChosen();
    // Everything in view loads at once; the neighbours after the first view.
    void this.firstView().then(() => {
      if (this.state !== 'destroyed') this.afterFirstView();
    });
  }

  protected override moreNames(): readonly Placed[] {
    const labels = this.labelSpots.filter((_, i) => i !== this.chosen);
    return this.panelSpot ? [this.panelSpot, ...labels] : labels;
  }

  /** The panel and the other worlds' names as the painted map would place them with the camera on `cam` (clear of the controls and
   *  of each other), the shown ones' boxes on screen. */
  private namesAt(cam: Camera): Rect[] {
    const spots = [this.panelSpot, ...this.labelSpots.filter((_, i) => i !== this.chosen)].filter((p): p is Placed => !!p);
    const items = spots.map((p) => {
      const spot = { x: cam.x + p.mx * cam.zoom, y: cam.y + p.my * cam.zoom };
      return {
        spot,
        want: { x: spot.x + p.box.x, y: spot.y + p.drop * cam.zoom + p.box.y, w: p.box.w, h: p.box.h },
        maxShift: p.maxShift,
        slide: p.tappable,
        group: 0,
      };
    });
    return placeLabels(items, this.view, this.keepOut, this.edgeClear()).flatMap((p) => (p.shown ? [p.at] : []));
  }

  /**
   * Stand the pins and the glints for the stop the camera is on or going to, once (a stop chosen, a new screen size, a pin's words
   * changed): the painting under them moves through a drag and a glide, they move with it, and what a stop shows or hides fades in or
   * out. A pin's ring is on its spot (but for a place no stop shows otherwise, `slidePlaces`); its plate is moved off the controls
   * (overviewPlaces `layoutPins`).
   */
  private layPins(): void {
    const places = this.places;
    if (!places || !this.lay || this.view.w <= 1) return;
    const cam = stopCamera(this.lay, this.view, this.chosen);
    const k = glintScale(this.lay.zoom);
    places.setGlintScale(k);
    const out = layoutPins(
      this.view,
      this.keepOut,
      this.namesAt(cam),
      artAt(cam),
      places.pins.map((pin) => ({
        id: pin.spot.id,
        spot: toScreen(cam, spotAt(pin.spot.id)!),
        plateW: pin.plateWidth,
        slide: this.slide.has(pin.spot.id),
      })),
      GLINT_IDS.map((id) => ({ id, spot: toScreen(cam, spotAt(id)!) })),
      k,
    );
    const first = this.pinTargets.size === 0;
    for (const pin of places.pins) {
      const at = out.pins[pin.spot.id]!;
      if (at.shown) {
        pin.setRingOffset(at.ringOffset.x, at.ringOffset.y);
        pin.setPlateOffset(at.offset.x, at.offset.y);
        // Its world's floor, for its plate (asking starts the load; floorIn brings it).
        this.floorFor(spotWorld(pin.spot));
      }
      this.pinTargets.set(pin.spot.id, at.shown);
      if (this.reduced || first) {
        pin.alpha = at.shown ? 1 : 0;
        pin.visible = at.shown;
      }
      pin.eventMode = at.shown ? 'static' : 'none';
    }
    for (const id of GLINT_IDS) places.blockGlint(id, !out.glints[id]!.shown, this.reduced || first);
  }

  /** The pins' fades, `ms` on. */
  private fadePins(ms: number): void {
    const step = ms / PIN_FADE_MS;
    for (const pin of this.places?.pins ?? []) {
      const target = this.pinTargets.get(pin.spot.id) ? 1 : 0;
      if (pin.alpha === target) continue;
      pin.alpha = pin.alpha < target ? Math.min(target, pin.alpha + step) : Math.max(target, pin.alpha - step);
      pin.visible = pin.alpha > 0;
    }
  }

  protected override resized(first: boolean): void {
    this.places?.layout(this.view.w, this.view.h);
    if (first) {
      this.places?.showTitle();
    } else if (this.lay) {
      this.apply(stopCamera(this.lay, this.view, this.chosen));
      this.vx = idealX(this.lay, this.chosen);
      this.loadFade();
    }
    this.panel?.setCompact(this.lay?.anchors.compact ?? false);
    this.panel?.setArrowsBeside(this.lay?.anchors.arrowsBeside ?? true);
    if (this.panelSpot) this.panelSpot.box = boxOf(this.panel!);
    this.syncControls();
  }

  /** The strip's controls, from the screen's anchors (mapHud): the dots, the Wheel and the gift. */
  protected override layoutMore(w: number, h: number, inset: MapInsets): Container[][] {
    this.lay = stripLayout({ w, h }, inset);
    this.slide = slidePlaces(this.lay, { w, h }, inset);
    const a = this.lay.anchors;
    if (this.dots) {
      this.dots.visible = a.dots;
      this.dots.position.set((w - 6 * DOT_STEP) / 2, a.dotsY ?? 0);
    }
    const groups: Container[][] = [];
    if (this.dots?.visible) groups.push([this.dots]);
    if (this.places) {
      const [cx, cy] = crownAnchor({ w, h }, inset);
      this.places.crown.position.set(cx, cy);
      this.places.gift.position.set(cx, cy + 84);
      groups.push([this.places.crown, this.places.gift].filter((c) => c.visible));
    }
    return groups;
  }

  protected override moreControls(out: Map<string, Container>): void {
    if (this.places) {
      out.set('wheel', this.places.crown);
      out.set('gift', this.places.gift);
    }
    const panel = this.panel;
    if (panel) {
      out.set('visit', panel.visitButton);
      out.set('arrowWest', panel.arrow(-1));
      out.set('arrowEast', panel.arrow(1));
    }
    this.dots?.children.forEach((c, i) => out.set(`dot${i}`, c as Container));
    this.labelSpots.forEach((l, i) => out.set(`name${i}`, l.item));
  }

  protected override escapeOverlay(): boolean {
    return this.places?.escape() ?? false;
  }

  protected override covering(): boolean {
    return !!this.places?.covering;
  }

  protected override hostCovered(covered: boolean): void {
    this.places?.setCovered(covered);
    this.lamps?.setPaused(covered);
  }

  protected override cameraMoved(zoom: number): void {
    this.places?.onCamera(zoom);
    for (const pin of this.places?.pins ?? []) pin.scale.set(1 / zoom);
  }

  protected override floorIn(world: number, texture: Texture): void {
    this.places?.floorArrived(world, texture);
    if (world === this.currentWorld) this.panel?.setFloor(texture);
  }

  protected override disposeMore(): void {
    for (const t of [this.scrollTimer, this.enterTimer]) if (t) clearTimeout(t);
    this.scrollTimer = null;
    this.enterTimer = null;
    this.places?.destroy();
    this.places = null;
    this.pinTargets.clear();
    this.lamps?.destroy();
    this.lamps = null;
    for (const key of Object.keys(this.sceneSprites)) this.sceneSprites[Number(key)] = null;
    this.sceneWanted.clear();
    this.fadeWanted.clear();
    this.fadeBottomWanted.clear();
    this.sceneLoadOf.clear();
    this.flightToken++;
    // The big files go with the scene; only those that were loaded (unloading another warns).
    const drop = (url: string): void => {
      if (Assets.cache.has(url)) void Assets.unload(url).catch(() => undefined);
    };
    for (const w of STRIP_WORLDS) drop(vignetteUrl(w));
    for (let i = 0; i < FADE_SLICES; i++) {
      drop(fadeUrl(i));
      drop(fadeBottomUrl(i));
    }
    // The glow's own texture is this scene's (the painted lamps' are shared and cached): freed once the stage has gone with the app.
    const dot = this.lampArt?.dot;
    if (dot) setTimeout(() => dot.destroy(true), 0);
  }

  protected override tick(ms: number): void {
    this.fadePins(ms);
    this.places?.stepGlints(ms);
    this.note?.update(ms);
    this.lamps?.setPaused(this.mapCovered());
    this.lamps?.update(ms);
    if (this.pendingFresh.size > 0 && this.prefetch && !this.gliding() && !this.dragging) this.lightFresh();
  }

  /** The camera came to rest: bring in what is within half a view. */
  protected override viewCentred(_x: number, _y: number): void {
    if (!this.prefetch) return;
    this.loadScenes(this.view.w / this.cam.zoom / 2);
    this.loadFade();
  }

  /** The title plaque ("The Goodtime Realms") hangs on the first view; the first thing that moves the painting (a drag, a step, a wheel, a
   *  sideways scroll) takes it down, so it never hangs over the next world's scene as the player pans. */
  private dismissTitle(): void {
    void this.places?.hideTitle();
  }

  // ---- the chosen world ----

  private worldAt(i: number): StripWorld {
    return STRIP_WORLDS[i]!;
  }

  /** Make a world the chosen one and bring it to the middle (the snap, or a tap on its name). A world whose picture is not in yet (a
   *  fling two stops on) is waited for, a moment at most, so its clearing never shows empty. */
  private choose(i: number, ms = SNAP_MS): void {
    if (!this.lay || this.entering !== null) return;
    this.dismissTitle();
    this.chosen = i;
    this.currentWorld = this.worldAt(i).biome;
    this.vx = idealX(this.lay, i);
    this.refreshChosen();
    this.syncControls();
    const token = ++this.flightToken;
    const wait = this.sceneSprites[i] ? null : this.startScene(i);
    this.loadScenes(this.view.w / this.cam.zoom / 2);
    if (!wait) {
      this.flightWait = false;
      this.flyTo(stopCamera(this.lay, this.view, i), ms);
      return;
    }
    this.flightWait = true;
    void Promise.race([wait, new Promise<void>((resolve) => setTimeout(resolve, SCENE_WAIT_MS))]).then(() => {
      if (token !== this.flightToken || this.state === 'destroyed') return;
      this.flightWait = false;
      if (this.lay && this.entering === null) this.flyTo(stopCamera(this.lay, this.view, this.chosen), ms);
    });
  }

  private centre(i: number): void {
    if (i !== this.chosen) this.choose(i);
  }

  private step(by: number): void {
    const to = stepWorld(this.chosen, by);
    // At the road's end nothing changes, but the press or the wheel event that came first may have stopped a glide: go on to the stop.
    if (to !== this.chosen) this.choose(to);
    else this.resumeFlight();
  }

  /** A drag or a scroll let go: settle on the stop its speed carries it to. */
  private settleVirtual(v: number): void {
    if (!this.lay) return;
    this.settleOn(snapWorld(this.lay, this.vx, v, this.dragFrom));
  }

  /** Settle on world `to`: glide back to the chosen world's stop, or choose another. */
  private settleOn(to: number): void {
    if (!this.lay) return;
    if (to === this.chosen) {
      this.vx = idealX(this.lay, to);
      this.flyTo(stopCamera(this.lay, this.view, to), SNAP_MS);
    } else this.choose(to);
  }

  /** The panel and the dots say the chosen world. */
  private refreshChosen(): void {
    const sw = this.worldAt(this.chosen);
    const slots = worldSlots(sw.biome);
    const open = slots.filter((n) => isSlotUnlocked(n.id, this.unlocked)).length;
    if (this.panel) {
      this.panel.set(sw.name, open, slots.length);
      const floor = this.floorFor(sw.biome);
      if (floor) this.panel.setFloor(floor);
      if (this.panelSpot) {
        this.panelSpot.mx = sw.stop;
        this.panelSpot.world = sw.biome;
        this.panelSpot.box = boxOf(this.panel);
      }
    }
    this.labelSpots.forEach((l, i) => {
      // The chosen world's name is its panel; a name coming back fades in again.
      if (i === this.chosen) {
        l.item.alpha = 0;
        l.item.visible = false;
      } else if (!l.item.visible) l.item.alpha = 0;
    });
    this.dots?.setChosen(this.chosen);
    this.panel?.setEnds(this.chosen > 0, this.chosen < STRIP_WORLDS.length - 1);
    if (this.state === 'initialized') this.placeNames();
  }

  protected override edgeClear(): number {
    return EDGE;
  }

  /** The controls stand for the screen, and the names and pins for the stop. */
  private syncControls(): void {
    if (!this.lay) return;
    if (this.state === 'initialized' || this.view.w > 1) this.layoutHud();
    this.placeNames();
    this.layPins();
  }

  // ---- into a world ----

  private mapped(biome: number): boolean {
    return this.opts.overview.worlds.includes(biome);
  }

  private visit(): void {
    this.enter(this.chosen);
  }

  /** Glide in on a scene and open the world's map (the board, for a world without one). */
  private enter(i: number): void {
    // (A drag never reaches here: a scene's own tap checks `dragged`, and the Visit button is not on the painting.)
    if (!this.lay || this.entering !== null) return;
    const sw = this.worldAt(i);
    if (!this.mapped(sw.biome)) {
      this.openBoard(sw.biome);
      return;
    }
    this.entering = i;
    this.opts.overview.onEnterWorld?.(sw.biome);
    if (!this.reduced) this.flyTo(zoomedCamera(this.lay, this.view, i), FLY_MS);
    this.enterTimer = setTimeout(
      () => {
        this.enterTimer = null;
        this.entering = null;
        if (this.state !== 'destroyed') this.opts.overview.onOpenWorld(sw.biome, null);
      },
      this.reduced ? 0 : FLY_MS,
    );
  }

  private sceneTapped(i: number): void {
    if (this.dragged || this.entering !== null) return;
    if (i === this.chosen) this.enter(i);
    else this.choose(i);
  }

  // ---- loading ----

  /** What the first view needs, started at once: the scenes in view and the top fade's slices. */
  private loadFirstView(): void {
    this.loadScenes(0, FIRST_VIEW_SHARE);
    this.loadFade();
  }

  private afterFirstView(): void {
    this.firstViewDoneAt = performance.now();
    this.prefetch = true;
    this.loadScenes(this.view.w / this.cam.zoom / 2);
    this.loadFade();
    this.apply(this.cam);
    if (this.easeOut && this.lay) {
      this.easeOut = false;
      this.flyTo(stopCamera(this.lay, this.view, this.chosen), FLY_MS);
    }
    this.startLamps();
  }

  /** Bring in the scenes that touch the view grown by `extra` master pixels each side. */
  private loadScenes(extra: number, share = 0): void {
    const rect = viewRect(this.cam, this.view);
    for (const i of scenesIn(rect, extra, share)) void this.startScene(i);
  }

  /** Start a scene's picture loading (once); resolves when it is in or has failed. */
  private startScene(i: number): Promise<void> {
    const had = this.sceneLoadOf.get(i);
    if (had) return had;
    this.sceneWanted.add(i);
    const sw = this.worldAt(i);
    const p = Assets.load<Texture>(vignetteUrl(sw))
      .then((tex) => {
        if (this.state === 'destroyed') return;
        const s = new Sprite(tex);
        s.position.set(sw.sprite.x, sw.sprite.y);
        s.label = `scene:${sw.name}`;
        s.eventMode = 'static';
        s.cursor = 'pointer';
        s.hitArea = new Polygon(sw.tap.flatMap(([x, y]) => [x - sw.sprite.x, y - sw.sprite.y]));
        s.on('pointertap', () => this.sceneTapped(i));
        this.sceneLayer.addChild(s);
        this.sceneSprites[i] = s;
      })
      .catch(() => undefined);
    this.sceneLoadOf.set(i, p);
    this.sceneLoads.push(p);
    return p;
  }

  /** The fades' slices in view: the top one when the apron shows above the painting, the bottom one when it shows below (a tall
   *  phone shows it below, under the panel). */
  private loadFade(): void {
    if (!this.lay) return;
    const rect = viewRect(this.cam, this.view);
    const top = apronAbove(this.lay.y);
    const bottom = apronBelow(this.lay.y, this.lay.zoom, this.view.h);
    for (let i = 0; i < FADE_SLICES; i++) {
      const x = i * FADE_SLICE_W;
      if (x + FADE_SLICE_W < rect.x || x > rect.x + rect.w) continue;
      if (top && !this.fadeWanted.has(i)) {
        this.fadeWanted.add(i);
        this.sceneLoads.push(this.loadSlice(fadeUrl(i), x, -FADE_H, (s) => this.fadeSprites.set(i, s)));
      }
      if (bottom && !this.fadeBottomWanted.has(i)) {
        this.fadeBottomWanted.add(i);
        this.sceneLoads.push(this.loadSlice(fadeBottomUrl(i), x, STRIP_H, (s) => this.fadeBottomSprites.set(i, s)));
      }
    }
  }

  private loadSlice(url: string, x: number, y: number, keep: (s: Sprite) => void): Promise<void> {
    return Assets.load<Texture>(url)
      .then((tex) => {
        if (this.state === 'destroyed') return;
        const s = new Sprite(tex);
        s.position.set(x, y);
        this.fadeLayer.addChild(s);
        keep(s);
      })
      .catch(() => undefined);
  }

  // ---- the lamps ----

  private startLamps(): void {
    if (this.places?.realm) return;
    // The realm view comes through onRealm; without one (offline) the remembered set stands.
    void this.placesReady?.then(() => {
      if (this.state === 'destroyed' || this.places?.realm) return;
      this.applyLit(litWorlds(this.remembered, null, this.homeIdx));
    });
  }

  private placesReady: Promise<void> | null = null;

  /** The realm view arrived: light the worlds it says were played. */
  private realmIn(view: RealmView): void {
    const mem = this.opts.lamps;
    const played = (mem?.worldsPlayed(view) ?? []).flatMap((b) => {
      const i = stripIndexOf(b);
      return i >= 0 ? [i] : [];
    });
    this.applyLit(litWorlds(this.remembered, played, this.homeIdx));
  }

  /** `now` should be lit: worlds remembered stay lit; the newly reached light lamp by lamp once they are in view. */
  private applyLit(now: readonly number[]): void {
    const { fresh } = lampMoment(this.remembered, now);
    const had = new Set(this.remembered);
    if (fresh.length === 0) return;
    if (this.reduced || !this.lamps) {
      this.lamps?.setLit([...had, ...fresh]);
      this.remember([...had, ...fresh]);
      return;
    }
    for (const w of fresh) this.pendingFresh.add(w);
  }

  /** The newly reached worlds with a lamp in view light up now, and are remembered. */
  private lightFresh(): void {
    const rect = viewRect(this.cam, this.view);
    const go = freshInView(this.pendingFresh, rect);
    if (go.length === 0) return;
    for (const w of go) this.pendingFresh.delete(w);
    this.lamps?.lightUp(go);
    this.remember([...this.remembered, ...go]);
  }

  private remember(worlds: readonly number[]): void {
    this.remembered = [...new Set(worlds)].sort((a, b) => a - b);
    this.opts.lamps?.save(this.remembered.map((i) => this.worldAt(i).biome));
  }

  private lampTapped(world: number, lamp: Lamp): void {
    if (this.dragged || !this.note || !this.lay) return;
    const lit = this.lamps?.isLit(world) ?? false;
    const at = toScreen(this.cam, lamp.foot);
    this.note.say(lampLine(this.worldAt(world).name, lit), { x: at.x, y: at.y - 232 * this.cam.zoom }, this.view, this.floorFor(this.worldAt(world).biome));
  }

  // ---- the side places, the secrets and the title (the Wheel and the gift are the realm's; pins and glints are the places stream's) ----

  private buildPlaces(opts: RealmPlacesOptions): void {
    const places = new RealmPlaces(
      {
        stage: this.app.stage,
        ticker: this.app.ticker,
        floor: (world) => this.floorFor(world),
        bezel: () => this.texture('bezel'),
        lock: () => this.texture('lock'),
        // A press that is a drag taps nothing, and nothing opens while a world is being entered.
        dragged: () => this.dragged || this.entering !== null,
        world: () => this.currentWorld,
        coverChanged: () => this.pauseAmbient(),
        relayout: () => this.measurePins(),
      },
      { ...opts, positions: { ...stripPositions(), ...opts.positions }, onRealm: (v) => this.realmIn(v) },
      this.map,
    );
    this.places = places;
    places.setCovered(this.hostPaused);
    BIOMES.forEach((_, w) => {
      const t = this.floors.loaded(w);
      if (t) places.floorArrived(w, t);
    });
    // Glints sit on the painting over the scenes and lamps and under the names; pins over everything on it.
    this.world.addChildAt(places.glintLayer, this.world.getChildIndex(this.labelLayer));
    this.world.addChild(this.spotLayer);
    for (const pin of places.pins) {
      // Not shown until a stop has placed it (no flash of a pin where it will not be), at screen size.
      pin.alpha = 0;
      pin.visible = false;
      pin.eventMode = 'none';
      pin.scale.set(1 / this.cam.zoom);
      this.spotLayer.addChild(pin);
    }
    this.hud.addChild(places.crown, places.gift);
    this.app.stage.addChild(places.overlay);
    this.placesReady = places.init();
    void this.placesReady;
  }

  /** A pin's look changed (its plate's words): measure it again. */
  private measurePins(): void {
    if (this.state === 'initialized') this.layPins();
  }

  /** Fetch the player's realm again (the player has just loaded). */
  reloadPlaces(): void {
    void this.places?.reload();
  }

  /** The daily gift is there to claim, or isn't. */
  setGiftReady(ready: boolean): void {
    this.places?.setGiftReady(ready);
    if (this.state === 'initialized') {
      // The gift button stands under the Wheel: the controls' keep-out changed, so the pins stand again for this stop.
      this.layoutHud();
      this.layPins();
    }
  }

  // ---- test and dev hooks (screen points are CSS pixels in the canvas) ----

  /** When the first view had drawn (performance.now), or null before: files fetched by then are the first view's bytes. */
  firstViewTime(): number | null {
    return this.firstViewDoneAt;
  }

  /** The chosen world's name. */
  chosenName(): string {
    return this.worldAt(this.chosen).name;
  }

  /** The chosen world's place on the road, west (0) to east (6). */
  chosenIndex(): number {
    return this.chosen;
  }

  /** Put the camera over a point of the strip at the glide's zoom (the lamp sheets and stills); the next snap or step brings it back. */
  lookAt(mx: number, my: number): void {
    if (!this.lay) return;
    const zoom = this.lay.maxZoom;
    this.apply({ x: this.view.w / 2 - mx * zoom, y: this.view.h / 2 - my * zoom, zoom });
  }

  /** The camera as it is now. */
  camera(): Camera {
    return { ...this.cam };
  }

  /** The strip's layout for this screen, or null before it has one. */
  layoutNow(): { zoom: number; y: number } | null {
    return this.lay ? { zoom: this.lay.zoom, y: this.lay.y } : null;
  }

  /** The panel's words, or null while it is hidden. */
  panelUi(): { title: string; line: string; shown: boolean } | null {
    if (!this.panel || !this.panelSpot) return null;
    return { title: this.panel.title, line: this.panel.line, shown: this.panelSpot.shown };
  }

  /** Where a world's scene is on screen (a point on its painted part), or null while it is off screen. */
  scenePoint(name: string): { x: number; y: number } | null {
    const i = STRIP_WORLDS.findIndex((w) => w.name === name);
    if (i < 0) return null;
    const p = toScreen(this.cam, tapPoint(STRIP_WORLDS[i]!));
    return p.x > 0 && p.x < this.view.w && p.y > 0 && p.y < this.view.h ? p : null;
  }

  /** Scenes loaded, by world name. */
  loadedScenes(): string[] {
    return STRIP_WORLDS.flatMap((w, i) => (this.sceneSprites[i] ? [w.name] : []));
  }

  /** The names under the scenes, with where they are on screen (the chosen world's name is its panel, not listed). */
  sideNames(): { name: string; shown: boolean; rect: { x: number; y: number; w: number; h: number } }[] {
    return this.labelSpots.flatMap((l, i) => {
      if (i === this.chosen) return [];
      const b = l.item.getBounds();
      return [{ name: this.worldAt(i).name, shown: l.shown && l.item.visible && l.item.alpha > 0.5, rect: { x: b.x, y: b.y, w: b.width, h: b.height } }];
    });
  }

  /** Which of the panel's arrows show, and whether they stand beside the plate (else beside Visit). */
  arrows(): { west: boolean; east: boolean; beside: boolean } {
    return this.panel?.arrows() ?? { west: false, east: false, beside: false };
  }

  /** The camera is at the chosen world's stop. */
  atStop(): boolean {
    return this.onStopNow();
  }

  /** The bottom fade's slices loaded. */
  loadedFadeBottom(): number[] {
    return [...this.fadeBottomSprites.keys()].sort();
  }

  /** The top fade's slices loaded. */
  loadedFade(): number[] {
    return [...this.fadeSprites.keys()].sort();
  }

  /** The dots are shown. */
  dotsShown(): boolean {
    return !!this.dots?.visible;
  }

  /** Each lamp's level. */
  lampStates(): LampState[] {
    return this.lamps?.states() ?? [];
  }

  /** Where a world's lamp stands on screen, by world name and side. */
  lampPoint(name: string, side: 'west' | 'east'): { x: number; y: number } | null {
    const i = STRIP_WORLDS.findIndex((w) => w.name === name);
    const lamp = this.worldAt(Math.max(0, i)).lamps[side === 'west' ? 0 : 1];
    const p = toScreen(this.cam, [lamp[0], lamp[1] - 90]);
    return i >= 0 && p.x > 0 && p.x < this.view.w && p.y > 0 && p.y < this.view.h ? p : null;
  }

  /** The note a lamp's tap says now, or ''. */
  noteLine(): string {
    return this.note?.line ?? '';
  }

  /** The world being glided into, before its map opens, or null. */
  enteringWorld(): string | null {
    return this.entering === null ? null : this.worldAt(this.entering).name;
  }

  /** The player's realm as loaded. */
  realmView(): unknown {
    return this.places?.realm ?? null;
  }

  /** The title plaque is showing. */
  titleShown(): boolean {
    return this.places?.titleShown ?? false;
  }

  // ---- the side places and secrets (tests and dev) ----

  /**
   * Bring a side place or a secret into view. The strip's camera rests only on the worlds' stops (one axis, no zoom, the panel hangs
   * under the chosen scene), so this snaps to a stop and never centres on the spot itself: of the stops that show it, the one with
   * it nearest the screen's middle (logic/realm/overviewPlaces `focusOrder`, with estimated sizes), checked against the scene's real
   * placing, which takes the next stop when the estimate was wrong.
   */
  focusOn(id: string): void {
    if (!this.lay || !spotAt(id)) return;
    const order = focusOrder(this.lay, this.view, this.opts.insets ?? NO_INSET, id);
    // The estimate says which stops should show it; the stop's real placing (laid out as it is chosen) has the last word.
    for (const stop of order) {
      this.choose(stop);
      const pin = this.places?.pins.find((p) => p.spot.id === id);
      const shown = pin ? !!this.pinTargets.get(id) : !(this.places?.glintBlocked(id) ?? true);
      if (shown) return;
    }
    this.choose(order[0]!);
  }

  /** Where a side place's ring is on screen, or null while its pin is hidden (not shown at this stop, or still fading in). */
  spotPoint(id: string): { x: number; y: number } | null {
    const pin = this.places?.pins.find((p) => p.spot.id === id);
    if (!pin || !this.pinTargets.get(id) || pin.alpha < 0.5) return null;
    return this.places?.spotPoint(id) ?? null;
  }

  /** Where a pin is on screen: its ring (on its spot) and its plate (as moved), or null while it is hidden. */
  pinBoxes(id: string): { ring: Rect; plate: Rect; box: Rect } | null {
    const pin = this.places?.pins.find((p) => p.spot.id === id);
    if (!pin || !this.pinTargets.get(id) || pin.alpha < 0.5) return null;
    const b = pin.getBounds();
    const c = pin.toGlobal(pin.ringCentre());
    const r = { x: c.x - RING_R, y: c.y - RING_R, w: RING_R * 2, h: RING_R * 2 };
    const o = pin.plateOffsetNow;
    const pw = pin.plateWidth;
    const base = pin.toGlobal({ x: 0, y: 0 });
    const plate = { x: base.x - pw / 2 + o.x, y: base.y - 22 + o.y, w: pw, h: 20 };
    return { ring: r, plate, box: { x: b.x, y: b.y, w: b.width, h: b.height } };
  }

  /** Where a secret's glint is on screen, or null once found or while something is over it. */
  glintPoint(id: string): { x: number; y: number } | null {
    return this.places?.glintPoint(id) ?? null;
  }

  /** The open place card as plain data, or null. */
  placeCard(): PlaceCardUi | null {
    return this.places?.cardUi ?? null;
  }

  /** Where a place card's button is on screen. */
  placeCardButton(label: string): { x: number; y: number } | null {
    return this.places?.cardButtonPoint(label) ?? null;
  }

  /** The mini-game on screen as plain data, or null. */
  get miniGameUi(): MiniGameSnapshot | null {
    return this.places?.miniGameUi ?? null;
  }

  pressMiniGame(label: string): boolean {
    return this.places?.pressMiniGame(label) ?? false;
  }

  tapMiniGame(index: number): boolean {
    return this.places?.tapMiniGame(index) ?? false;
  }
}
